import React from 'react';
import { Header, Footer } from '../components/layout';
import { ShieldCheck, FileText } from 'lucide-react';

export const PrivacyPolicyPage: React.FC = () => {
  return (
    <div className="min-h-screen flex flex-col justify-between bg-slate-50 dark:bg-slate-950 transition-colors">
      <Header />
      <main className="flex-1 max-w-4xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-sm p-8 space-y-6">
          <div className="flex items-center gap-3 border-b border-slate-200 dark:border-slate-800 pb-6">
            <div className="w-12 h-12 rounded-xl bg-emerald-100 dark:bg-emerald-950/60 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
              <ShieldCheck className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
                Privacy Policy
              </h1>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                Zero passive browsing tracking. Zero selling of user data.
              </p>
            </div>
          </div>

          <div className="space-y-4 text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
              1. Information We Collect
            </h2>
            <p>
              PriceLoss collects only the specific product URLs you explicitly submit for tracking. We do not monitor your active browsing history, tab switches, cart contents, or checkout behavior.
            </p>

            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
              2. Account Information
            </h2>
            <p>
              When creating an account, we collect your email address and an argon2id-hashed representation of your password solely for authentication and price drop alert dispatching.
            </p>

            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
              3. Data Retention
            </h2>
            <p>
              Historical price observations recorded by our system are retained for aggregated price trend analysis. You may delete individual watches or your entire account at any time.
            </p>

            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
              4. AI & External Integrations
            </h2>
            <p>
              When connecting PriceLoss to an AI assistant (such as ChatGPT) or Model Context Protocol (MCP) client, access is authorized solely through OAuth 2.0 with explicitly consented scopes: <code>priceloss:read</code> (accessing account details, watches, price history, and alerts), <code>priceloss:write</code> (managing watches, alert settings, and requesting URL verifications), and <code>priceloss:events</code> (receiving confirmed price-drop notifications).
            </p>
            <p>
              Data shared with connected assistants is strictly limited to your active watches, confirmed price observations, and alert events. You may review and revoke active integrations at any time from your account dashboard; revoking an integration immediately invalidates issued access tokens. PriceLoss never sells user data, never transfers personal data to unauthorized third parties, and never monitors user browsing activity outside explicitly submitted URLs.
            </p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export const TermsPage: React.FC = () => {
  return (
    <div className="min-h-screen flex flex-col justify-between bg-slate-50 dark:bg-slate-950 transition-colors">
      <Header />
      <main className="flex-1 max-w-4xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-sm p-8 space-y-6">
          <div className="flex items-center gap-3 border-b border-slate-200 dark:border-slate-800 pb-6">
            <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-950/60 flex items-center justify-center text-blue-600 dark:text-blue-400">
              <FileText className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
                Terms of Service
              </h1>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                Terms governing consumer use of PriceLoss price monitoring
              </p>
            </div>
          </div>

          <div className="space-y-4 text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
              1. Service Scope
            </h2>
            <p>
              PriceLoss provides automated price observation and notification tools for informational purposes. While we strive for maximum accuracy, retailer prices fluctuate rapidly and final checkout price is governed by the respective retailer.
            </p>

            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
              2. Acceptable Use
            </h2>
            <p>
              Users agree not to submit malicious URLs, exploit crawler resources, or attempt denial-of-service against our infrastructure or third-party retailer hosts.
            </p>

            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
              3. AI Integrations & Non-Purchasing Scope
            </h2>
            <p>
              PriceLoss provides informational price alerts and assistive tools only. PriceLoss DOES NOT execute automated purchases, store payment cards, or conduct merchant checkout on your behalf.
            </p>
            <p>
              All purchases occur directly with independent retailers. Product information and price observations provided through our web application, browser extensions, or connected AI integrations are non-binding informational indicators. Users are responsible for confirming prices, terms, and shipping fees directly with retailers prior to purchase.
            </p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};
